import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CourseKeySearchInput } from '@/components/course-keys/CourseKeySearchInput';
import {
  CourseKeyUsersSheet,
  type CourseKeyUsersSheetTarget,
} from '@/components/course-keys/CourseKeyUsersSheet';
import { useDebounce } from '@/hooks/useDebounce';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { courseKeysApi } from '@/lib/api/courseKeys';
import { cn } from '@/lib/utils';
import type {
  CourseKeyConfig,
  CourseKeyCourseVersionStat,
  CourseKeyPlatform,
} from '@/types/course-key';
import { formatAdminBusinessEt } from '@/lib/admin-datetime';
import {
  formatTimePeriod,
  resolveAccessPeriod,
  resolveConfigDuration,
  resolveInactivePeriod,
} from '@/utils/courseKeyTime';

type PlatformFilter = 'all' | CourseKeyPlatform;

function programTitle(config: CourseKeyConfig): string {
  if (typeof config.programId === 'object' && config.programId?.title) {
    return config.programId.title;
  }
  return String(config.programId);
}

function categoryTitle(config: CourseKeyConfig): string {
  if (typeof config.categoryId === 'object' && config.categoryId?.title) {
    return config.categoryId.title;
  }
  return '—';
}

function templateVersion(config: CourseKeyConfig): number {
  return config.version ?? 1;
}

function templateEditCount(config: CourseKeyConfig): number {
  return Math.max(0, templateVersion(config) - 1);
}

function formatEditCount(count: number): string {
  if (count === 0) return 'No edits';
  if (count === 1) return '1 edit';
  return `${count} edits`;
}

function enrollmentLabel(platform: CourseKeyPlatform): string {
  return platform === 'ispring' ? 'Added on iSpring' : 'Enrolled (MindTap)';
}

function formatEnrollmentCount(count: number): string {
  return count === 1 ? '1 user' : `${count} users`;
}

function formatCourseVersionLabel(stat: CourseKeyCourseVersionStat): string {
  const courseLabel =
    stat.ispringCourseIds.length === 1
      ? '1 course'
      : `${stat.ispringCourseIds.length} courses`;
  return `v${stat.version} · ${courseLabel}${stat.isCurrent ? ' · current' : ''}`;
}

function UsersCell({
  config,
  onOpenUsers,
}: {
  config: CourseKeyConfig;
  onOpenUsers: (target: CourseKeyUsersSheetTarget) => void;
}) {
  const stats =
    config.platform === 'ispring' && (config.courseVersionStats?.length ?? 0) > 0
      ? config.courseVersionStats!
      : null;

  if (stats) {
    return (
      <div className="space-y-1.5">
        {stats.map((stat) => (
          <div key={stat.version}>
            <button
              type="button"
              className="text-left text-sm font-medium tabular-nums text-foreground underline-offset-2 hover:underline"
              onClick={() =>
                onOpenUsers({
                  config,
                  courseVersion: stat.version,
                  versionStat: stat,
                })
              }
            >
              {formatEnrollmentCount(stat.studentCount)}
            </button>
            <p className="text-xs text-muted-foreground">{formatCourseVersionLabel(stat)}</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className="text-left text-sm font-medium tabular-nums text-foreground underline-offset-2 hover:underline"
        onClick={() => onOpenUsers({ config })}
      >
        {formatEnrollmentCount(config.enrollmentCount ?? 0)}
      </button>
      <p className="text-xs text-muted-foreground">{enrollmentLabel(config.platform)}</p>
    </>
  );
}

function reactivationSummary(config: CourseKeyConfig): string {
  if (config.platform === 'mindtap') return '—';
  const fee = config.reactivationFee;
  if (!fee) return '—';
  const inactive = resolveInactivePeriod(fee);
  const access = resolveAccessPeriod(fee);
  return `$${(fee.amountCents / 100).toFixed(2)} after ${formatTimePeriod(inactive)} inactive → ${formatTimePeriod(access)} access`;
}

function statusBadge(status: string) {
  if (status === 'active') return <Badge>Active</Badge>;
  if (status === 'draft') return <Badge variant="secondary">Draft</Badge>;
  return <Badge variant="outline">Archived</Badge>;
}

type Props = {
  active: boolean;
  platformFilter: PlatformFilter;
  onPlatformFilterChange: (filter: PlatformFilter) => void;
  onEdit: (config: CourseKeyConfig) => void;
  onCreateClick: () => void;
  refreshToken?: number;
};

export function CourseKeyListTab({
  active,
  platformFilter,
  onPlatformFilterChange,
  onEdit,
  onCreateClick,
  refreshToken = 0,
}: Props) {
  const [items, setItems] = useState<CourseKeyConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [usersSheetTarget, setUsersSheetTarget] = useState<CourseKeyUsersSheetTarget | null>(
    null
  );
  const debouncedSearch = useDebounce(searchInput.trim(), 400);

  const load = useCallback(
    async (silent = false) => {
      if (!active) return;
      try {
        if (silent) setRefreshing(true);
        else setLoading(true);
        const data = await courseKeysApi.list({
          page: 1,
          limit: 100,
          platform: platformFilter === 'all' ? undefined : platformFilter,
          search: debouncedSearch || undefined,
        });
        setItems(data.items || []);
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Failed to load course keys');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [active, debouncedSearch, platformFilter]
  );

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const handleArchive = async (config: CourseKeyConfig) => {
    try {
      await courseKeysApi.updateStatus(config._id, 'archived');
      toast.success('Course key archived');
      void load(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Archive failed');
    }
  };

  const filterButtons: { id: PlatformFilter; label: string }[] = [
    { id: 'all', label: 'All Keys' },
    { id: 'ispring', label: 'iSpring Keys' },
    { id: 'mindtap', label: 'MindTap Keys' },
  ];

  if (!active) return null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>List View</CardTitle>
            <CardDescription>All course key templates created by admins.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => void load(true)} disabled={refreshing}>
              <RefreshCw className={cn('mr-2 h-4 w-4', refreshing && 'animate-spin')} />
              Refresh
            </Button>
            <Button size="sm" onClick={onCreateClick}>
              + Create Course Key
            </Button>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="inline-flex flex-wrap gap-1 rounded-full border p-1">
            {filterButtons.map((btn) => (
              <button
                key={btn.id}
                type="button"
                onClick={() => onPlatformFilterChange(btn.id)}
                className={cn(
                  'rounded-full px-3 py-1.5 text-sm transition-colors',
                  platformFilter === btn.id
                    ? 'bg-foreground text-background'
                    : 'text-muted-foreground hover:bg-muted'
                )}
              >
                {btn.label}
              </button>
            ))}
          </div>
          <CourseKeySearchInput
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search by program name…"
            aria-label="Search course keys by program name"
          />
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {debouncedSearch ? 'No course keys match your search.' : 'No course keys yet.'}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Program</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Delivery</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Edits</TableHead>
                <TableHead>Users</TableHead>
                <TableHead>Reactivation</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((config) => (
                <TableRow key={config._id}>
                  <TableCell className="font-medium">{programTitle(config)}</TableCell>
                  <TableCell className="capitalize">
                    {config.platform === 'ispring' ? 'iSpring' : 'MindTap'}
                  </TableCell>
                  <TableCell>{categoryTitle(config)}</TableCell>
                  <TableCell className="max-w-[140px] truncate">
                    {config.deliveryModes.join(', ')}
                  </TableCell>
                  <TableCell>{formatTimePeriod(resolveConfigDuration(config))}</TableCell>
                  <TableCell>
                    <Badge variant="outline">v{templateVersion(config)}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {formatEditCount(templateEditCount(config))}
                  </TableCell>
                  <TableCell>
                    <UsersCell config={config} onOpenUsers={setUsersSheetTarget} />
                  </TableCell>
                  <TableCell className="max-w-[180px] truncate text-sm text-muted-foreground">
                    {reactivationSummary(config)}
                  </TableCell>
                  <TableCell>{statusBadge(config.status)}</TableCell>
                  <TableCell>
                    {config.updatedAt ? formatAdminBusinessEt(config.updatedAt) : '—'}
                  </TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button variant="ghost" size="sm" onClick={() => onEdit(config)}>
                      Edit
                    </Button>
                    {config.status !== 'archived' && (
                      <Button variant="ghost" size="sm" onClick={() => void handleArchive(config)}>
                        Archive
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <CourseKeyUsersSheet
        open={usersSheetTarget !== null}
        onOpenChange={(open) => {
          if (!open) setUsersSheetTarget(null);
        }}
        target={usersSheetTarget}
      />
    </Card>
  );
}
